import * as angular from 'angular';
import { escape } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import { SelectOption } from '@mode/shared/ui-angularjs';
import { TableListData } from '@mode/shared/ui-angularjs';
import { FieldDescriptor, TableListSchema } from '@mode/shared/ui-angularjs';
import {
  TableListAvatarValue,
  TableListHtmlValue,
  TableListTextValue,
  TableListValueType,
} from '@mode/shared/ui-angularjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MembershipHelpers, MembershipResource } from '@mode/legacy/data-access-model-js';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';

import { MembershipActionOption } from '../members-browser/members-browser.component';
import { getAppName, formatTimeAgo } from '@mode/shared/util-js';

enum MemberListFieldName {
  Avatar = 'avatar',
  Name = 'name',
  Email = 'email',
  Username = 'username',
  Status = 'status',
  ManagedBy = 'managed',
}

interface MemberListRecord {
  [MemberListFieldName.Avatar]: TableListAvatarValue['data'];
  [MemberListFieldName.Name]: TableListTextValue['data'];
  [MemberListFieldName.Email]: TableListHtmlValue['data'];
  [MemberListFieldName.Username]: TableListHtmlValue['data'];
  [MemberListFieldName.Status]: TableListTextValue['data'];
  [MemberListFieldName.ManagedBy]?: TableListTextValue['data'];
}

const DEFAULT_FIELD_DESCRIPTORS: FieldDescriptor[] = [
  {
    name: MemberListFieldName.Avatar,
    title: 'User',
    type: TableListValueType.Avatar,
  },
  {
    name: MemberListFieldName.Name,
    title: '',
    type: TableListValueType.Html,
    tooltip: (cell) => cell[MemberListFieldName.Name],
  },
  {
    name: MemberListFieldName.Email,
    title: 'Email',
    type: TableListValueType.Html,
    tooltip: (cell) => cell[MemberListFieldName.Email],
  },
  {
    name: MemberListFieldName.Username,
    title: 'Username',
    type: TableListValueType.Html,
    tooltip: (cell) => cell[MemberListFieldName.Username],
  },
  {
    name: MemberListFieldName.Status,
    title: 'Status',
    type: TableListValueType.Text,
  },
  {
    name: MemberListFieldName.ManagedBy,
    title: 'Managed By',
    type: TableListValueType.Text,
  },
];

const ADMIN_FIELD_DESCRIPTOR_NAMES: FieldDescriptor['name'][] = [MemberListFieldName.ManagedBy];

export class MembersListController {
  static $inject = ['FeatureFlagsFacade'];

  readonly appName = getAppName();
  private members!: MembershipResource[];
  private memberActionOptionsHash!: { [token: string]: MembershipActionOption[] };
  private onMemberAction!: ($args: { $action: any; $token: string }) => void;
  private scimEnabled!: boolean;
  private adminEnabled!: boolean;
  private tableListDataSource$ = new BehaviorSubject<TableListData<MemberListRecord>[]>([]);
  private tableListSchema!: TableListSchema;
  private dropdownTooltipMessage!: string;
  private scimText = 'SCIM';

  constructor(private featureFlagsFacade: FeatureFlagsFacade) {
    this.featureFlagsFacade.asObservable(FeatureFlag.AzureActiveDirectoryScim).subscribe((enabled) => {
      this.scimText = enabled ? 'SCIM' : 'Okta SCIM';
    });
  }

  static getMemberStatus(member: MembershipResource): string {
    if (MembershipHelpers.isAdmin(member)) {
      return 'Admin';
    } else if (MembershipHelpers.isPending(member) && MembershipHelpers.hasValidInvite(member)) {
      return `Invite sent (Expires ${formatTimeAgo(member.invite_expires_at)})`;
    } else if (MembershipHelpers.isPending(member) && MembershipHelpers.hasExpiredInvite(member)) {
      return 'Expired invited';
    } else if (MembershipHelpers.isPending(member)) {
      return 'Invited';
    } else if (MembershipHelpers.isActive(member) && MembershipHelpers.isLimited(member)) {
      return 'Limited member';
    } else if (MembershipHelpers.isActive(member)) {
      return 'Member';
    } else if (MembershipHelpers.isBanned(member) && MembershipHelpers.hasActivatedAt(member)) {
      return 'Not active';
    } else if (MembershipHelpers.isBanned(member) && MembershipHelpers.isInviteCancelled(member)) {
      return 'Invite canceled';
    } else if (MembershipHelpers.isBanned(member)) {
      return 'Approval declined';
    } else if (MembershipHelpers.isRequested(member)) {
      return 'Requested access';
    }
    return '';
  }

  $onInit() {
    this.tableListSchema = this.getTableListSchema();
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes['members']) {
      this.tableListDataSource$.next(this.tableListData(changes['members'].currentValue));
    }
    this.dropdownTooltipMessage = `This Member is managed through SCIM and cannot be modified in ${this.appName}`;
  }

  tableListData(members: MembershipResource[]): TableListData<MemberListRecord>[] {
    return members.map((member) => ({ values: this.createMemberListRecord(member) }));
  }

  createMemberListRecord(member: MembershipResource): MemberListRecord {
    return {
      [MemberListFieldName.Avatar]: { avatar: member.avatar, size: 30 } as any,
      [MemberListFieldName.Name]: escape(member.name),
      [MemberListFieldName.Email]: member.email,
      [MemberListFieldName.Username]: member.username,
      [MemberListFieldName.Status]: MembersListController.getMemberStatus(member),
      [MemberListFieldName.ManagedBy]: this.getMemberManaged(member),
    };
  }

  getMemberActionOptions(rowIndex: number): MembershipActionOption[] {
    return this.memberActionOptionsHash[this.getTokenFromIndex(rowIndex)];
  }

  getTokenFromIndex(index: number) {
    return this.members[index] ? this.members[index].member_token : '';
  }

  getMemberManaged(member: MembershipResource): string {
    if (MembershipHelpers.isManagedByScim(member)) {
      return this.scimText;
    } else {
      return 'Mode';
    }
  }

  getTableListSchema(): TableListSchema {
    let columns = DEFAULT_FIELD_DESCRIPTORS;

    if (!this.adminEnabled || !this.scimEnabled) {
      columns = this.excludeAdminColumns(columns);
    }

    return { fields: columns };
  }

  excludeAdminColumns(columns: FieldDescriptor[]): FieldDescriptor[] {
    return columns.filter((c) => !ADMIN_FIELD_DESCRIPTOR_NAMES.includes(c.name));
  }

  handleRowActionOptionSelection($option: SelectOption, $rowIndex: number) {
    this.onMemberAction({ $action: $option.value, $token: this.members[$rowIndex].member_token });
  }
}

export const MembersListComponent: angular.IComponentOptions = {
  controller: MembersListController,
  controllerAs: 'ctrl',
  bindings: {
    members: '<',
    memberActionOptionsHash: '<',
    onMemberAction: '&',
    scimEnabled: '<',
    adminEnabled: '<',
    dropdownTooltipMessage: '<',
  },
  template: `
    <div class="members-list">
      <table-list
        schema="ctrl.tableListSchema"
        dropdown-enabled="true"
        on-row-dropdown-init="ctrl.getMemberActionOptions($index)"
        on-row-dropdown-select="ctrl.handleRowActionOptionSelection($option, $index)"
        data="ctrl.tableListDataSource$ | async:this"
        dropdown-tooltip-message="ctrl.dropdownTooltipMessage"
        dropdown-tooltip-enabled="ctrl.MembershipHelpers.isManagedByScim(member)"
      />
    </div>
  `,
};
